import axios from "axios";
import { isTenantHost } from "@/lib/tenantHost";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Multi-tenant ---
// Le slug est détecté depuis l'URL `/t/{slug}/...` ; sinon défaut "emilie".
// Il est exposé via header `X-Tenant-Slug` ET via préfixe d'URL pour souplesse.

const DEFAULT_TENANT_SLUG = "emilie";

// --- The remembered admin practice (D3-Operator contract §5.3) ---
// The bare `/admin` tree on a platform host does not name its practice: it sends the slug
// remembered here (resolved by /saas/my-tenant, persisted across reloads). Two rules keep
// that memory from opening a practice the account never chose on the bare tree:
//   1. only the bare tree writes it: on an address whose PATH names the practice
//      (`/t/{slug}/...`), remembering it would make a later bare-/admin tab silently open
//      the last practice an operator managed. Enforced here, for every writer.
//   2. a value written before D3 is not trusted: every visit to `/t/X/admin` used to
//      remember X under LEGACY_ADMIN_SLUG_KEY. The memory moved to a new key and the
//      legacy one is deleted on load.
export const ADMIN_SLUG_STORAGE_KEY = "solunary_bare_admin_slug";
const LEGACY_ADMIN_SLUG_KEY = "solunary_admin_slug";

// Same slug grammar as admin/adminPath.js and lib/tenantPath.js.
const PATH_NAMED_PRACTICE = /^\/t\/([a-z0-9](?:[a-z0-9-]{0,58}[a-z0-9])?)(?:\/|$)/;

const pathNamedSlug = () => {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(PATH_NAMED_PRACTICE);
  return m ? m[1] : null;
};

let resolvedAdminSlug = null;
try {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(LEGACY_ADMIN_SLUG_KEY);
    resolvedAdminSlug = localStorage.getItem(ADMIN_SLUG_STORAGE_KEY);
  }
} catch (e) {
  if (process.env.NODE_ENV !== "production") console.warn("[api] localStorage read failed:", e?.message);
}

const writeAdminSlug = (slug) => {
  resolvedAdminSlug = slug || null;
  try {
    if (typeof localStorage !== "undefined") {
      if (slug) localStorage.setItem(ADMIN_SLUG_STORAGE_KEY, slug);
      else localStorage.removeItem(ADMIN_SLUG_STORAGE_KEY);
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("[api] localStorage write failed:", e?.message);
  }
};

/**
 * Remember `slug` as the bare `/admin` tree's practice. Does nothing on an address whose
 * path names its practice (rule 1 above), and for an empty slug.
 */
export const rememberAdminTenantSlug = (slug) => {
  if (!slug || pathNamedSlug()) return;
  writeAdminSlug(slug);
};

/** Forget the bare `/admin` tree's practice (sign-out, a cache the server rejected). */
export const forgetAdminTenantSlug = () => writeAdminSlug(null);

export const getCurrentTenantSlug = () => {
  if (typeof window === "undefined") return DEFAULT_TENANT_SLUG;
  const path = window.location.pathname;
  const named = pathNamedSlug();
  if (named) return named;
  // Zone admin : UNIQUEMENT le slug du tenant authentifié. Jamais de repli sur le
  // slug de démo — il désignerait le cabinet d'un autre praticien (et, comme le
  // backend faisait lui aussi un repli silencieux, un vrai client aurait lu/écrit
  // les données de la démo). null ⇒ en-tête omis ⇒ le backend refuse (fail-closed).
  if (path.startsWith("/admin")) return resolvedAdminSlug || null;
  return DEFAULT_TENANT_SLUG;
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Intercepteur: injecte le slug courant dans le header pour chaque requête.
// Phase C: sur un host tenant (domaine custom ou sous-domaine), on N'ENVOIE PAS
// X-Tenant-Slug — le backend résout le tenant par le Host (X-Forwarded-Host).
api.interceptors.request.use((config) => {
  try {
    config.headers = config.headers || {};
    if (isTenantHost() || config.url?.startsWith("/marketplace")) {
      delete config.headers["X-Tenant-Slug"];
    } else {
      const slug = getCurrentTenantSlug();
      // slug null (zone admin, tenant pas encore résolu) → on N'ENVOIE RIEN plutôt
      // que d'adresser un autre cabinet ; le backend répond TENANT_CONTEXT_REQUIRED.
      if (slug) config.headers["X-Tenant-Slug"] = slug;
      else delete config.headers["X-Tenant-Slug"];
    }
  } catch (e) {
    // Tenant slug detection failure is non-fatal — log for telemetry but don't break the request
    if (process.env.NODE_ENV !== "production") console.warn("[api] tenant slug detection failed:", e?.message);
  }
  return config;
});

// Adresse renommée : le middleware répond 301 vers le nouvel hôte quand on appelle
// l'API depuis un ancien sous-domaine (former_subdomains). Le XHR suit la redirection
// (CORS couvre les sous-domaines de la plateforme), mais les requêtes préflightées
// (POST JSON…) ne survivent pas à une 301 en pleine chaîne — on bascule donc le
// NAVIGATEUR entier vers le nouvel hôte dès qu'on détecte un host final différent.
api.interceptors.response.use((response) => {
  try {
    const finalURL = response?.request?.responseURL;
    if (finalURL) {
      // Garde anti-boucle : si REACT_APP_BACKEND_URL pointe déjà hors-origine
      // (API cross-origin assumée), un host final différent est NORMAL, pas un
      // renommage — on ne redirige que quand l'API est same-origin.
      const apiHost = new URL(API, window.location.origin).host;
      const finalHost = new URL(finalURL).host;
      if (finalHost && apiHost === window.location.host && finalHost !== window.location.host) {
        window.location.replace(
          `${window.location.protocol}//${finalHost}${window.location.pathname}${window.location.search}`
        );
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("[api] host-move detection failed:", e?.message);
  }
  return response;
});

// extractApiError lives in ./apiErrors — a pure module with no axios dependency, so it
// can be imported for real under Jest (api.js itself never can: see apiErrors.js's
// header comment). Re-exported here so none of this file's ~40 existing callers need
// to change their import path.
export { extractApiError } from "./apiErrors";

export const API_URL = API;
export const DEFAULT_TENANT = DEFAULT_TENANT_SLUG;
